// client/src/components/EditPOI_parts/EditPOI_MainForm.js
import React, { useMemo, useEffect } from 'react';
import { padHexColour } from './EditPOI_utils';

function looksLikeIconUrl(v) {
  const s = String(v ?? '').trim();
  if (!s) return false;
  const lower = s.toLowerCase();
  if (lower.startsWith('data:image/')) return true;
  if (lower.startsWith('http://') || lower.startsWith('https://')) return true;
  if (lower.startsWith('/')) return true;
  if (/\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(lower)) return true;
  return false;
}

export default function EditPOIMainForm({
  poi,
  canEdit,
  onStartMovePoi,
  onClose,
  edit,
  overlayOptions,
  customCategories,
  typesArray,
  selectedOverlayId,
  setSelectedOverlayId,
  lockOverlay,
  setLockOverlay,
  labelStyle,
  openMoveModal,
  openTypeMoveModal,
  handleChange,
  handleDeleteImage,
  handleSave,
  dirty,
  saving,
  effectiveMapId,
  setMode,
  setImageFile,
  imageUploading,
  setDirty,
  handleDelete,
  allowIndividualPoiColours = false,
  availablePoiLayers = [],
}) {
  const xVal = Number(poi && (poi.x != null ? poi.x : poi.poi_x != null ? poi.poi_x : poi.poiX));
  const yVal = Number(poi && (poi.y != null ? poi.y : poi.poi_y != null ? poi.poi_y : poi.poiY));
  const xUi = Number.isFinite(xVal) ? xVal.toFixed(2) : '?';
  const yUi = Number.isFinite(yVal) ? yVal.toFixed(2) : '?';

  const showNumberControls = ['number', 'number_label'].includes(String(edit.visualStyle || '').trim().toLowerCase());
  const hasLayersUi = Array.isArray(availablePoiLayers) && availablePoiLayers.length > 0;

  const hasIconForMarkerStyle = useMemo(() => {
    const typeIcon = edit?.iconUrl;
    if (looksLikeIconUrl(typeIcon)) return true;

    const typeIconAlt = edit?.typeIconUrl ?? edit?.type_icon_url ?? edit?.typeIcon;
    if (looksLikeIconUrl(typeIconAlt)) return true;

    const poiImage = edit?.imageUrl;
    if (looksLikeIconUrl(poiImage)) return true;

    const poiImageAlt = edit?.poiImageUrl ?? edit?.poi_image_url;
    if (looksLikeIconUrl(poiImageAlt)) return true;

    return false;
  }, [
    edit?.iconUrl,
    edit?.typeIconUrl,
    edit?.type_icon_url,
    edit?.typeIcon,
    edit?.imageUrl,
    edit?.poiImageUrl,
    edit?.poi_image_url,
  ]);

  useEffect(() => {
    const v = String(edit?.visualStyle || '').trim().toLowerCase();
    if (v !== 'icon_label' && v !== 'icon_label_always') return;
    if (hasIconForMarkerStyle) return;
    handleChange('visualStyle', 'icon_only');
  }, [edit?.visualStyle, hasIconForMarkerStyle, handleChange]);

  const ALL_SHAPES = [
    { value: 'circle', label: 'Circle' },
    { value: 'square', label: 'Square' },
    { value: 'triangle', label: 'Triangle' },
    { value: 'diamond', label: 'Diamond' },
    { value: 'hex', label: 'Hex' },
    { value: 'octagon', label: 'Octagon' },
    { value: 'pin', label: 'Map Pin' },
    { value: 'flag', label: 'Flag' },
    { value: 'crosshair', label: 'Crosshair' },
    { value: 'shield', label: 'Shield' },
    { value: 'badge', label: 'Badge' },
    { value: 'tag', label: 'Tag' },
    { value: 'key', label: 'Key' },
    { value: 'lock', label: 'Lock' },
    { value: 'unlock', label: 'Unlock' },
    { value: 'info', label: 'Info' },
    { value: 'plus', label: 'Plus' },
    { value: 'bolt', label: 'Bolt' },
    { value: 'drop', label: 'Drop' },
    { value: 'wrench', label: 'Wrench' },
    { value: 'cone', label: 'Cone' },
    { value: 'pill', label: 'Pill' },
    { value: 'star', label: 'Star' },
    { value: 'heart', label: 'Heart' },
  ];

  const numberOptions = [
    ...Array.from({ length: 99 }, (_, i) => ({ value: i + 1, label: String(i + 1) })),
    ...Array.from({ length: 26 }, (_, i) => {
      const value = 100 + i;
      const label = String.fromCharCode(65 + i);
      return { value, label };
    }),
  ];

  return (
    <div
      style={{
        background: '#232427',
        borderRadius: 16,
        boxShadow: '0 6px 24px #000b',
        padding: 22,
        color: '#e2e4e9',
        width: '100%',
        maxWidth: 820,
        minWidth: 0,
        fontSize: 15,
        fontFamily: 'inherit',
        margin: '0 auto',
        pointerEvents: 'auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 24,
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          pointerEvents: 'auto',
        }}
      >
        <div style={{ flex: '1 1 320px', minWidth: 0, pointerEvents: 'auto' }}>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>
              <b>Name:</b>
            </label>
            <input
              value={edit.poiName}
              onChange={e => handleChange('poiName', e.target.value)}
              disabled={!canEdit}
              autoFocus
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>
              <b>Category:</b>
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                list="categories-list"
                value={edit.category}
                onChange={e => handleChange('category', e.target.value)}
                disabled={!canEdit}
                style={{ background: '#333', color: '#fff', flex: 1 }}
              />
              {canEdit && (
                <button
                  type="button"
                  onMouseDown={e => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    openMoveModal();
                  }}
                  style={{
                    backgroundColor: '#20c067',
                    color: '#101314',
                    border: 'none',
                    borderRadius: 6,
                    padding: '6px 14px',
                    cursor: 'pointer',
                    fontWeight: 800,
                    fontSize: 13,
                    height: 32,
                    whiteSpace: 'nowrap',
                    pointerEvents: 'auto',
                  }}
                >
                  Move
                </button>
              )}
              <datalist id="categories-list">
                {Array.isArray(customCategories) &&
                  customCategories.map(c => <option key={c.id ?? c.name} value={c.name} />)}
                {typesArray.map(t => {
                  const cn = (t.categoryName || '').trim();
                  return cn ? <option key={`t_${t.id}_${cn}`} value={cn} /> : null;
                })}
              </datalist>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>
              <b>Type:</b>
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                list="types-list"
                value={edit.typeName}
                onChange={e => handleChange('typeName', e.target.value)}
                disabled={!canEdit}
                style={{ flex: 1 }}
              />
              {canEdit && (
                <button
                  type="button"
                  onMouseDown={e => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    openTypeMoveModal();
                  }}
                  style={{
                    backgroundColor: '#20c067',
                    color: '#101314',
                    border: 'none',
                    borderRadius: 6,
                    padding: '6px 14px',
                    cursor: 'pointer',
                    fontWeight: 800,
                    fontSize: 13,
                    height: 32,
                    whiteSpace: 'nowrap',
                    pointerEvents: 'auto',
                  }}
                >
                  Move
                </button>
              )}
            </div>
            <datalist id="types-list">
              {(() => {
                const cat = (edit.category || '').trim().toLowerCase();
                const names = typesArray
                  .filter(t => (t.categoryName || '').trim().toLowerCase() === cat)
                  .map(t => t.name)
                  .filter(Boolean)
                  .sort((a, b) => a.localeCompare(b));
                return names.map(n => <option key={n} value={n} />);
              })()}
            </datalist>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>
              <b>Description:</b>
            </label>
            <textarea
              rows={5}
              value={edit.description}
              onChange={e => handleChange('description', e.target.value)}
              disabled={!canEdit}
              style={{ width: '100%', fontSize: 15, fontFamily: 'inherit' }}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>
              <b>Linked Map:</b>
            </label>
            <input
              type="number"
              value={edit.linkedMapId || ''}
              onChange={e => handleChange('linkedMapId', e.target.value ? Number(e.target.value) : '')}
              disabled={!canEdit}
              placeholder="(none)"
              style={{ width: 160 }}
            />
          </div>

          <div style={{ marginTop: 10, pointerEvents: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', pointerEvents: 'auto' }}>
              <div style={{ fontSize: 12, color: '#c9cbcf' }}>
                x: {xUi} / y: {yUi}
              </div>

              <button
                type="button"
                onMouseDown={e => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();

                  try {
                    if (typeof onStartMovePoi === 'function') {
                      onStartMovePoi(poi);
                    } else {
                      window.dispatchEvent(new CustomEvent('pa-poi-move-start', { detail: { poi } }));
                    }
                  } catch {}

                  try {
                    if (typeof onClose === 'function') onClose();
                  } catch {}
                }}
                style={{
                  background: '#20c067',
                  color: '#101314',
                  border: 'none',
                  borderRadius: 9,
                  padding: '6px 12px',
                  fontWeight: 800,
                  fontSize: 13,
                  cursor: 'pointer',
                  height: 32,
                  whiteSpace: 'nowrap',
                  opacity: 1,
                  pointerEvents: 'auto',
                }}
                title="Move this POI"
              >
                Move POI
              </button>
            </div>

            <div style={{ marginTop: 6, fontSize: 11, color: '#c9cbcf' }}>
              Click a new position on the map to update this POI&apos;s location.
            </div>
          </div>
        </div>

        <div style={{ flex: '1 1 320px', minWidth: 0, pointerEvents: 'auto' }}>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>
              <b>Type Colour:</b>
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="color"
                value={edit.typeColour}
                onChange={e => handleChange('typeColour', padHexColour(e.target.value))}
                disabled={!canEdit}
              />
              <span style={{ fontSize: 13, color: '#ccc' }}>{edit.typeColour}</span>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>
              <b>Label Border:</b>
            </label>
            <select
              value={edit.labelBorder}
              onChange={e => handleChange('labelBorder', Number(e.target.value))}
              disabled={!canEdit}
            >
              <option value={1}>Yes</option>
              <option value={0}>No</option>
            </select>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>
              <b>Label Background:</b>
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="color"
                  value={edit.labelBgColour}
                  onChange={e => handleChange('labelBgColour', padHexColour(e.target.value))}
                  disabled={!canEdit || edit.useTransparentBg}
                  style={{ width: 80, height: 32 }}
                />
                <span style={{ fontSize: 13, color: '#ccc' }}>{edit.labelBgColour}</span>
              </div>
              <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="checkbox"
                  checked={!!edit.useTransparentBg}
                  onChange={e => handleChange('useTransparentBg', e.target.checked)}
                  disabled={!canEdit}
                />
                <span>Use transparent icon background</span>
              </label>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={labelStyle}>
                <b>Overlay:</b>
              </label>
              <select
                value={selectedOverlayId}
                onChange={e => {
                  setSelectedOverlayId(e.target.value);
                  setDirty(true);
                }}
                disabled={!canEdit || !lockOverlay}
                style={{
                  flex: 1,
                  background: '#333',
                  color: '#fff',
                  border: '1px solid #444',
                  borderRadius: 8,
                  padding: '6px 10px',
                }}
              >
                <option value="base">Base map (no overlay)</option>
                {Array.isArray(overlayOptions) &&
                  overlayOptions.map(o => {
                    const id = o.id ?? o.overlay_id ?? o.asset_id;
                    if (id == null) return null;
                    const label = o.title || o.name || `Overlay #${id}`;
                    return (
                      <option key={id} value={String(id)}>
                        {label}
                      </option>
                    );
                  })}
              </select>
            </div>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: 13,
                marginLeft: labelStyle.minWidth ? labelStyle.minWidth : 0,
              }}
            >
              <input
                type="checkbox"
                checked={lockOverlay}
                disabled={!canEdit}
                onChange={e => {
                  setLockOverlay(e.target.checked);
                  setDirty(true);
                }}
                style={{ marginRight: 6 }}
              />
              <span>Lock POI to selected overlay / base map (otherwise show on all overlays)</span>
            </label>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>
              <b>Marker Style:</b>
            </label>
            <select
              value={edit.visualStyle || 'shape_label'}
              onChange={e => handleChange('visualStyle', e.target.value)}
              disabled={!canEdit}
              style={{ width: '100%' }}
            >
              <option value="shape_label">Shape + Label</option>
              <option value="shape_only">Shape Only</option>
              <option value="auto">Shape Only - Label on Mouseover</option>
              <option value="number">Number</option>
              <option value="number_label">Number - Label on Mouseover</option>
              <option value="icon_only">Icon/Label Only</option>
              {hasIconForMarkerStyle && <option value="icon_label">Icon - Label on Mouseover</option>}
              {hasIconForMarkerStyle && <option value="icon_label_always">Icon + Label</option>}
            </select>
          </div>

          {showNumberControls ? (
            <>
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>
                  <b>Number:</b>
                </label>
                <select
                  value={Number(edit.numberValue || 1)}
                  onChange={e => handleChange('numberValue', Number(e.target.value))}
                  disabled={!canEdit}
                  style={{ width: 140 }}
                >
                  {numberOptions.map(o => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>
                  <b>Badge Shape:</b>
                </label>
                <select
                  value={String(edit.numberBadgeShape || 'circle')}
                  onChange={e => handleChange('numberBadgeShape', e.target.value)}
                  disabled={!canEdit}
                >
                  {ALL_SHAPES.map(s => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          ) : (
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>
                <b>Shape:</b>
              </label>
              <select
                value={String(edit.markerShape || 'circle')}
                onChange={e => handleChange('markerShape', e.target.value)}
                disabled={!canEdit}
              >
                {ALL_SHAPES.map(s => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {allowIndividualPoiColours && (
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>
                <b>POI Colour Override:</b>
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={!!edit.usePoiColourOverride}
                    onChange={e => handleChange('usePoiColourOverride', !!e.target.checked)}
                    disabled={!canEdit}
                  />
                  <span>{edit.usePoiColourOverride ? 'Using override' : 'No override (use type colour)'}</span>
                </label>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="color"
                    value={padHexColour(edit.poiColour || edit.typeColour || '#ff0000')}
                    onChange={e => handleChange('poiColour', padHexColour(e.target.value))}
                    disabled={!canEdit || !edit.usePoiColourOverride}
                    style={{ cursor: canEdit && edit.usePoiColourOverride ? 'pointer' : 'not-allowed' }}
                  />
                  <span style={{ fontSize: 13, color: '#ffe670' }}>
                    {String(padHexColour(edit.poiColour || edit.typeColour || '#ff0000')).toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          )}

          {hasLayersUi && (
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>
                <b>POI Layer:</b>
              </label>
              <select
                value={edit.lockPoiLayer ? String(edit.poiLayerKey || '') : ''}
                onChange={e => {
                  const v = String(e.target.value || '').trim();
                  handleChange('poiLayerKey', v);
                  handleChange('lockPoiLayer', !!v);
                }}
                disabled={!canEdit}
                style={{ width: 220 }}
              >
                <option value="">All layers</option>
                {availablePoiLayers.map(l => (
                  <option key={String(l?.id)} value={String(l?.id)}>
                    {String(l?.label || l?.id)}
                  </option>
                ))}
              </select>

              <label style={{ display: 'flex', alignItems: 'center', fontSize: 13, marginTop: 6 }}>
                <input
                  type="checkbox"
                  checked={!!edit.lockPoiLayer}
                  onChange={e => {
                    const next = !!e.target.checked;
                    if (!next) {
                      handleChange('lockPoiLayer', false);
                      handleChange('poiLayerKey', '');
                      return;
                    }
                    const first = availablePoiLayers.length ? String(availablePoiLayers[0]?.id || '') : '';
                    handleChange('poiLayerKey', String(edit.poiLayerKey || '').trim() || first);
                    handleChange('lockPoiLayer', true);
                  }}
                  disabled={!canEdit}
                  style={{ marginRight: 6 }}
                />
                <span>{edit.lockPoiLayer ? 'Lock to selected layer' : 'Show on all layers'}</span>
              </label>
            </div>
          )}

          <div style={{ marginTop: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
              <div style={{ marginBottom: 6 }}>
                <b>Type Icon:</b>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {edit.iconUrl ? (
                  <img
                    src={edit.iconUrl}
                    alt="Type Icon"
                    style={{ width: 32, height: 32, borderRadius: 4, border: '1px solid #444' }}
                    onError={e => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <span style={{ color: '#888', fontSize: 14 }}>No icon</span>
                )}
                {canEdit && (
                  <button
                    type="button"
                    onMouseDown={e => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setMode('icon-upload');
                    }}
                    style={{
                      background: '#20c067',
                      color: '#101314',
                      border: 'none',
                      borderRadius: 6,
                      padding: '6px 12px',
                      cursor: 'pointer',
                      fontWeight: 700,
                      fontSize: 13,
                      pointerEvents: 'auto',
                    }}
                  >
                    Change
                  </button>
                )}
              </div>
            </div>

            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
              <div style={{ marginBottom: 6 }}>
                <b>POI Image:</b>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {edit.imageUrl ? (
                  <img
                    src={edit.imageUrl}
                    alt="POI"
                    style={{
                      maxWidth: 120,
                      maxHeight: 70,
                      borderRadius: 5,
                      border: '1px solid #555',
                    }}
                    onError={e => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <span style={{ color: '#888', fontSize: 14 }}>No image</span>
                )}
              </div>
              {canEdit && (
                <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onMouseDown={e => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setMode('image-upload');
                    }}
                    style={{
                      background: '#20c067',
                      color: '#101314',
                      border: 'none',
                      borderRadius: 6,
                      padding: '6px 12px',
                      cursor: 'pointer',
                      fontWeight: 700,
                      fontSize: 13,
                      pointerEvents: 'auto',
                    }}
                  >
                    Change
                  </button>
                  <button
                    type="button"
                    onMouseDown={e => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDeleteImage();
                    }}
                    disabled={!edit.imageUrl || imageUploading}
                    style={{
                      background: !edit.imageUrl ? '#444' : '#f44c2b',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      padding: '6px 10px',
                      cursor: !edit.imageUrl ? 'not-allowed' : 'pointer',
                      fontWeight: 800,
                      fontSize: 13,
                      pointerEvents: 'auto',
                    }}
                    title={!edit.imageUrl ? 'No image to delete' : undefined}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: 22,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          pointerEvents: 'auto',
        }}
      >
        {canEdit && (
          <button
            type="button"
            onMouseDown={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleDelete();
            }}
            style={{
              background: '#f44c2b',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '8px 22px',
              fontWeight: 700,
              fontSize: 15,
              cursor: 'pointer',
              pointerEvents: 'auto',
            }}
          >
            Delete
          </button>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, pointerEvents: 'auto' }}>
          <button
            type="button"
            onMouseDown={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            style={{
              background: '#333',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '8px 22px',
              fontWeight: 700,
              fontSize: 15,
              cursor: 'pointer',
              pointerEvents: 'auto',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onMouseDown={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleSave();
            }}
            disabled={!dirty || saving || !effectiveMapId}
            style={{
              background: dirty ? '#20c067' : '#666',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '8px 22px',
              fontWeight: 700,
              fontSize: 15,
              cursor: dirty ? 'pointer' : 'not-allowed',
              pointerEvents: 'auto',
            }}
            title={!effectiveMapId ? 'No map_id available' : undefined}
          >
            {saving ? 'Saving…' : 'Save POI'}
          </button>
        </div>
      </div>
    </div>
  );
}
